import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useMutation } from '@apollo/client';
import { connect, useDispatch } from 'react-redux';
import { usePathname } from 'next/navigation';
import { noTopSafeArea, useBridgeState } from '@digitaz/bridge-sdk-client';
import * as actionCreators from '../../actions';
import {
  togglePromotionModalView,
  toggleReplenishmentNotificationsModal
} from '../../actions';
import { sendAccessToken, readNotification } from '../../api/mutations';
import { BlockerDetector } from '../Banner/BlockerDetector';
import { Loader } from '../Loader';
import { useBackHistoryStorage } from '../../hooks/useBackHistoryStorage';
import {
  longPromotionHashes,
  shortPromotionHashes,
  urlHashes
} from '../../utils/helpers/constants';
import useScreenSize from '../../hooks/useScreenSize';
import { useFirstInteraction } from '../../hooks/useFirstInteraction';
import { useAutoScrollTop } from '../../hooks/useAutoScrollTop';
import { WelcomeBanner } from '../ReplenishmentWelcomeBanner';
import { Footer } from '../Footer';
import { MainLayout } from '../ui/Layout';
import { InitialFlexibleHeader } from '../FlexibleHeader/InitialFlexibleHeader';
import { HeaderWrapperBlock } from './styled';
import { RootState } from '../../store/types';
import { LayoutProps } from './types';
import {
  AuthMutation,
  AuthMutationVariables,
  ReadNotificationMutation,
  ReadNotificationMutationVariables
} from '../../graphql/graphql';
import { useGoogleMaps } from '../../providers/GoogleMapsProvider';

const BannersContainer = dynamic(() => import('../Banner/BannersContainer'), {
  ssr: false
});

const FlexibleHeader = dynamic(
  () => import('../FlexibleHeader').then(mod => mod.FlexibleHeader),
  { ssr: false, loading: InitialFlexibleHeader }
);

const PromotionModalView = dynamic(
  () => import('../PromotionModalView').then(mod => mod.PromotionModalView),
  {
    ssr: false
  }
);

const SideMenuModal = dynamic(
  () => import('../SideMenuModal').then(mod => mod.SideMenuModal),
  { ssr: false }
);

const MapModal = dynamic(
  () => import('../ui/MapModal').then(mod => mod.MapModal),
  {
    ssr: false
  }
);

const AuthModal = dynamic(() => import('../AuthModal'), {
  ssr: false
});

const TabBarNavigation = dynamic(() =>
  import('../TabBarNavigation').then(mod => mod.TabBarNavigation)
);

let isLoading: boolean | null = null;
const mapStateToProps = (state: RootState) => ({
  isVisible: state.drawerUI.isVisible,
  isSideMenuVisible: state.sideMenuModal?.isVisible,
  isPromotionModalView: state.promotionModalView?.isVisible,
  isAuthVisible: state?.authModal?.isVisible,
  replenishmentNotifications: state?.replenishmentNotifications,
  showNotificationsModal: state?.replenishmentNotificationsModal?.isVisible
});

const LayoutComponent: React.FC<LayoutProps> = ({
  isBanner,
  children,
  setBlockerDetector,
  toggleSideMenuModal,
  isAuthVisible,
  isSideMenuVisible,
  isPromotionModalView,
  toggleAuthModal,
  isBottomNavigation = true,
  isShowHeader = true,
  isFixedHeader = false,
  isShowCloseButton = true,
  isCurrentItemPage = false,
  isHomePage = false,
  isSafeAreaTop = false,
  replenishmentNotifications,
  showNotificationsModal,
  isBackgroundBanner = false,
  isShowFooter = true,
  hideMobileHeader = false,
  isShowLocations = true
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { isWebView } = useBridgeState();
  const { isSmall } = useScreenSize();
  const { isInitializedMap } = useGoogleMaps();
  const hasUserInteracted = useFirstInteraction();
  const shouldRenderBanners = !isHomePage || hasUserInteracted;
  const pathname = usePathname();
  const hasAuthHash = router.asPath.includes(urlHashes.authentication);

  const isSearchPage = router.pathname === '/[...search]';
  const isAdCreateEditPage =
    router.pathname === '/items/new' || router.pathname === '/items/edit';

  useEffect(() => {
    if (isWebView) document.body.classList.add('webview');

    if (isSafeAreaTop && isWebView) {
      document.body.classList.add('safe-area-top');
    } else {
      document.body.classList.remove('safe-area-top');
    }
  }, [isSafeAreaTop, isWebView]);

  const closeAuthModalHandler = () => {
    toggleAuthModal?.(false);

    router.push(`${router.asPath.split('#')[0]}`, undefined, {
      shallow: true
    });
  };

  const accessToken = Array.isArray(router?.query.access_token)
    ? router.query.access_token[0]
    : router?.query.access_token;
  const [isNotificationShow] = useMutation<
    ReadNotificationMutation,
    ReadNotificationMutationVariables
  >(readNotification);
  const [mutateToken] = useMutation<AuthMutation, AuthMutationVariables>(
    sendAccessToken,
    {
      onCompleted: () => {
        isLoading = false;
      }
    }
  );

  useBackHistoryStorage();

  useAutoScrollTop(isCurrentItemPage, pathname);

  useEffect(() => {
    if (!isWebView) return;

    noTopSafeArea('', url => router.replace(url));

    const handleBeforeHistoryChange = (path: string) => {
      noTopSafeArea(path, url => router.replace(url));
    };

    router.events.on('beforeHistoryChange', handleBeforeHistoryChange);

    return () => {
      router.events.off('beforeHistoryChange', handleBeforeHistoryChange);
    };
  }, [isWebView]);

  useEffect(() => {
    if (router?.isReady) {
      if (accessToken) {
        isLoading = true;
        mutateToken({
          variables: { accessToken }
        });
      } else {
        isLoading = false;
      }
    }
  }, [accessToken]);

  useEffect(() => {
    toggleAuthModal?.(!!router.asPath.match(urlHashes.authentication));
    toggleSideMenuModal?.(!!router.asPath.match(urlHashes.more));

    dispatch(
      togglePromotionModalView(
        shortPromotionHashes.some(hash => router.asPath.match(hash))
      )
    );

    const handleHashChange = (url: string) => {
      toggleAuthModal?.(!!url.match(urlHashes.authentication));
      toggleSideMenuModal?.(!!url.match(urlHashes.more));

      dispatch(
        togglePromotionModalView(
          !!longPromotionHashes.some(hash => url.match(hash))
        )
      );
    };

    router?.events.on('hashChangeComplete', handleHashChange);

    return () => {
      router?.events.off('hashChangeComplete', handleHashChange);
    };
  }, []);

  const isAuthInProgress =
    (!router?.isReady && isLoading === null && !accessToken) ||
    (!!accessToken && isLoading !== false);

  const closeNotificationsModal = () => {
    dispatch(toggleReplenishmentNotificationsModal(false));

    if (replenishmentNotifications?.length) {
      isNotificationShow({
        variables: { id: replenishmentNotifications[0]?.id }
      });
    }
  };

  return (
    <MainLayout isWebView={isWebView}>
      {!!replenishmentNotifications?.length && (
        <WelcomeBanner
          isOpen={showNotificationsModal}
          hide={closeNotificationsModal}
          id={replenishmentNotifications[0]?.id}
          description={replenishmentNotifications[0]?.description}
          title={replenishmentNotifications[0]?.title}
          imageUrl={replenishmentNotifications[0]?.imageUrl}
        />
      )}
      {shouldRenderBanners && (
        <BannersContainer
          {...{ isBackgroundBanner, isBanner, isCurrentItemPage }}
        />
      )}
      {isShowHeader && (
        <HeaderWrapperBlock
          $isSearchPage={isSearchPage}
          $isCurrentItemPage={isCurrentItemPage}
          $isAdCreateEditPage={isAdCreateEditPage}
          $hideMobileHeader={hideMobileHeader}
        >
          <FlexibleHeader
            isFixedHeader={isWebView || isFixedHeader}
            isShowCloseButton={isShowCloseButton}
            isAuthInProgress={isAuthInProgress}
          />
        </HeaderWrapperBlock>
      )}
      <BlockerDetector setBlockerDetector={setBlockerDetector} />
      {children}
      <Loader pageLoader={true} />
      {isBottomNavigation && (
        <TabBarNavigation isAuthInProgress={isAuthInProgress} />
      )}
      {!(isWebView || isSideMenuVisible) && isShowFooter && (
        <Footer
          isTabBarExist={isBottomNavigation}
          isBackgroundBanner={isBackgroundBanner && !isSmall}
          isShowLocations={isShowLocations}
        />
      )}
      {isSideMenuVisible && <SideMenuModal />}
      {isPromotionModalView && <PromotionModalView pageScenario="ad_page" />}
      {isAuthVisible && hasAuthHash && (
        <AuthModal
          isOpen={isAuthVisible}
          modalId="auth"
          closeHandler={closeAuthModalHandler}
        />
      )}
      {isInitializedMap && <MapModal />}
    </MainLayout>
  );
};

export const Layout = connect(mapStateToProps, actionCreators)(LayoutComponent);
